import type {
  BusinessActivityResource,
  BusinessWithAssignmentResource,
  GetWorklayerJobExtractionStatusResponse,
  IWorklayerJobAPI,
  RentalActivityResource,
  RentalWithAssignmentResource,
  TaxActivityAssigneeResource,
} from '@taxfyle/txf-api-client'
import type { Id } from '@/infra/id'
import { Id as IdUtil } from '@/infra/id'
import { match } from '@/utils/pattern-match-util'
import {
  TaxPreparationData,
  TxfWorklayerJob,
  type TaxPreparationDataProps,
} from './models'
import {
  mapWorklayerJobResourceToProps,
  mapTaxPreparationDataResourceToProps,
  mapTaxPayerPropsToResource,
  mapSpousePropsToResource,
  mapDependentPropsToResource,
  mapBusinessActivityPropsToResource,
  mapRentalActivityPropsToResource,
  mapAssumedWorkpaperPropsToAssignmentResource,
  mapExtractionStatus,
} from './tax-prep-mappers'
import { collection } from '@/infra/collection'
import { NotFound } from 'fejl'

function mapTaxActivityAssigneeResource(
  assignee: TaxActivityAssigneeResource,
): { assigned_to?: string; joint_assigned_to?: string } {
  return match(assignee, 'type', {
    TAX_PAYER: ({ id }) => ({ assigned_to: id, joint_assigned_to: undefined }),
    SPOUSE: ({ id }) => ({ assigned_to: id, joint_assigned_to: undefined }),
    // eslint-disable-next-line @typescript-eslint/naming-convention
    JOINT: ({ tax_payer_id, spouse_id }) => ({
      assigned_to: tax_payer_id,
      joint_assigned_to: spouse_id,
    }),
    UNASSIGNED: () => ({
      assigned_to: undefined,
      joint_assigned_to: undefined,
    }),
  }) as { assigned_to?: string; joint_assigned_to?: string }
}

function mapBusinessToBusinessWithAssignmentResource(
  business: BusinessActivityResource,
): BusinessWithAssignmentResource {
  return {
    ...mapTaxActivityAssigneeResource(business.assigned_to),
    business,
  }
}

function mapRentalToRentalWithAssignmentResource(
  rental: RentalActivityResource,
): RentalWithAssignmentResource {
  return {
    ...mapTaxActivityAssigneeResource(rental.assigned_to),
    rental,
  }
}

export class TxfWorklayerJobStore {
  readonly txfWorklayerJobs = collection({
    create: () => new TxfWorklayerJob(),
    getModelId: (m) => m.id,
    getDataId: (d) => d.id,
  })

  constructor(private readonly _worklayerJobApi: IWorklayerJobAPI) {}

  async fetchTxfWorklayerJob(jobId: Id, signal?: AbortSignal) {
    signal?.throwIfAborted()
    const assertedJobId = IdUtil.assert(jobId)

    return this._worklayerJobApi
      .get(assertedJobId)
      .then((response) => {
        signal?.throwIfAborted()
        const props = mapWorklayerJobResourceToProps(response)
        return this.txfWorklayerJobs.set(props)
      })
      .catch(NotFound.ignore(null))
  }

  async fetchTxfTaxPreparationData(
    jobId: Id,
    year: number,
    signal?: AbortSignal,
  ) {
    signal?.throwIfAborted()
    const assertedJobId = IdUtil.assert(jobId)
    return this._worklayerJobApi
      .getTaxPreparationData(assertedJobId, year)
      .then((response) => {
        signal?.throwIfAborted()
        return new TaxPreparationData().set(
          mapTaxPreparationDataResourceToProps(response),
        )
      })
      .catch(NotFound.ignore(null))
  }

  async saveTxfTaxPreparationData(
    jobId: Id,
    year: number,
    data: TaxPreparationDataProps,
    signal?: AbortSignal,
  ): Promise<void> {
    signal?.throwIfAborted()
    const assertedJobId = IdUtil.assert(jobId)

    const basicInformationResource = {
      tax_payer: mapTaxPayerPropsToResource(data.basicInformation.taxPayer),
      spouse: data.basicInformation.spouse
        ? mapSpousePropsToResource(data.basicInformation.spouse)
        : undefined,
      dependents: data.basicInformation.dependents.map(
        mapDependentPropsToResource,
      ),
    }

    const businessResources = data.businesses.map(
      mapBusinessActivityPropsToResource,
    )
    const rentalResources = data.rentals.map(mapRentalActivityPropsToResource)
    const workpaperAssignments = data.workpapers.map(
      mapAssumedWorkpaperPropsToAssignmentResource,
    )

    await this._worklayerJobApi.createTaxPreparationData({
      job_id: assertedJobId,
      year,
      basic_information: basicInformationResource,
      tax_activities: {
        businesses: businessResources.map(
          mapBusinessToBusinessWithAssignmentResource,
        ),
        rentals: rentalResources.map(mapRentalToRentalWithAssignmentResource),
      },
      workpaper_assignments: workpaperAssignments,
    })
  }

  async getExtractionStatus(jobId: Id, signal?: AbortSignal) {
    signal?.throwIfAborted()
    const assertedJobId = IdUtil.assert(jobId)
    return this._worklayerJobApi
      .getExtractionStatus(assertedJobId)
      .then((response: GetWorklayerJobExtractionStatusResponse) => {
        signal?.throwIfAborted()
        return mapExtractionStatus(response)
      })
      .catch(NotFound.ignore(null))
  }
}
